import React, { useContext } from "react";
import { Image, Pressable, StyleSheet, View } from "react-native";
import Icon from "react-native-vector-icons/Ionicons";
import { FindifyDataContextType } from "@/contexts/FindifyDataContext";
import { FindifyMenuContext } from "@/contexts/FindifyMenuContext";
import { PlaceColors } from "../helpers/PlaceColors";
import { toggleSidemenu } from "./FindifyNavigation";

type BiscainhosAppHeaderProps = {
    navigation: any,
    findifyDataContext: FindifyDataContextType | null,
    rightIcon?: string,
    onRightPress?: () => void,
    showAccessibility?: boolean,
    allowBackFallback?: boolean,
}

export const BiscainhosAppHeader = ({
    navigation,
    findifyDataContext,
    rightIcon = "qr-code-outline",
    onRightPress,
    showAccessibility,
    allowBackFallback = true,
}: BiscainhosAppHeaderProps) => {
    const findifyMenuContext = useContext(FindifyMenuContext);
    const colors = PlaceColors(findifyDataContext);
    const logo = findifyDataContext?.workspace?.app_settings?.app_logo_url;
    const canOpenFullMenu = findifyMenuContext?.isSidemenu
        || findifyMenuContext?.hasSidemenuItems
        || (Number(findifyDataContext?.workspace?.workspace_id) === 155 && (findifyDataContext?.workspace?.app_settings?.app_menu_lines?.length ?? 0) > 4);
    const shouldShowAccessibility = showAccessibility ?? findifyDataContext?.visitVersion === "accessible";
    const canGoBack = allowBackFallback && navigation?.canGoBack?.();
    const goBackOrHome = () => {
        if (canGoBack) {
            navigation.goBack();
            return;
        }
        navigation.navigate("Place");
    }
    const openQrScanner = () => {
        if (onRightPress) {
            onRightPress();
            return;
        }
        navigation.push("Scan");
    }
    const openLanguageSelection = () => {
        navigation.navigate("Place", Number(findifyDataContext?.workspace?.workspace_id) === 155
            ? { selectLanguage: true }
            : undefined);
    }

    return <View style={[
        styles.header,
        {
            backgroundColor: colors.background.withAlpha(245),
            borderBottomColor: colors.primary.withAlpha(65),
        }
    ]}>
        {canOpenFullMenu ? (
            <Pressable style={styles.headerIconButton} onPress={() => toggleSidemenu(findifyMenuContext)}>
                <Icon name="menu-outline" size={30} color={colors.primary.get()} />
            </Pressable>
        ) : canGoBack ? (
            <Pressable style={styles.headerIconButton} onPress={goBackOrHome}>
                <Icon name="chevron-back" size={26} color={colors.primary.get()} />
            </Pressable>
        ) : (
            <Pressable style={styles.headerIconButton} onPress={goBackOrHome}>
                <Icon name="chevron-back" size={26} color={colors.primary.get()} />
            </Pressable>
        )}

        <Pressable
            onPress={openLanguageSelection}
            accessibilityRole="button"
            accessibilityLabel="Selecionar idioma">
            {logo ? <Image source={{ uri: logo }} resizeMode="contain" style={styles.logo} /> : <View style={styles.logo} />}
        </Pressable>

        <View style={styles.headerActions}>
            {shouldShowAccessibility ? (
                <Pressable style={[styles.accessibilityButton, { borderColor: colors.secondary.get() }]}>
                    <Icon name="accessibility" size={24} color={colors.primary.get()} />
                </Pressable>
            ) : null}
            <Pressable style={styles.headerIconButton} onPress={openQrScanner}>
                <Icon name={rightIcon} size={26} color={colors.primary.get()} />
            </Pressable>
        </View>
    </View>
}

const styles = StyleSheet.create({
    header: {
        height: 56,
        borderBottomWidth: 1,
        flexDirection: "row",
        alignItems: "center",
        justifyContent: "space-between",
        paddingHorizontal: 24,
        shadowColor: "#000",
        shadowOpacity: 0.08,
        shadowRadius: 6,
        shadowOffset: { width: 0, height: 2 },
        zIndex: 5,
    },
    headerIconButton: {
        width: 42,
        height: 42,
        alignItems: "center",
        justifyContent: "center",
    },
    logo: {
        width: 144,
        height: 34,
    },
    headerActions: {
        flexDirection: "row",
        alignItems: "center",
        gap: 10,
        minWidth: 42,
        justifyContent: "flex-end",
    },
    accessibilityButton: {
        width: 42,
        height: 42,
        borderRadius: 11,
        borderWidth: 1,
        alignItems: "center",
        justifyContent: "center",
    },
});
